import { Button, Dialog, Icon } from "armstrong-react";
import classNames from "classnames";
import moment from "moment";
import React from "react";
import Sticky from "react-stickynode";
import slugify from "speakingurl";
import _ from "underscore";
import { formatQueryString, getQueryStringValues } from '@travellocal/utils/urlHelpers'

import { COL_DEFAULT_MIN_WIDTH, IColumnDefinition, IFilterDefinition, ISortDefinition, ITableDefinition, SortState } from ".";
import { DropdownButton, Loader, PendingButton } from "../../../core/atoms/atoms";
import { isServer } from "../../../core/detection";
import { renderCsv } from "./csv";
import { RowActionList } from "./rowActionList";

import { ErrorBoundary } from "../errorBoundary";
import "./dataTable.scss";

let instanceCounter = 0;

/**
 * We need a unique #id for each component to pass to react-stickynode so its bottom boundary feature works
 * instanceCounter will be incremented each time InfiniteScroller is instantiated#
 * Will be inconsistent with HMR but should be fine with isomorphic render
 */
const getInstanceId = (): string => `infiniscroller-${++instanceCounter}`;

export enum InfiniteScrollerMode {
  EndlessOnScroll = 0,
  Manual,
  RetrieveAll,
}

interface OwnProps<TItem, TApiParams = {}> {
  getPage?: (page: number, filters?: TApiParams) => Promise<any>;
  startPageIndex?: number;
  tableDefinition: ITableDefinition<TItem, TApiParams>;
  title?: string;
  onClick?: (item: TItem) => void;
  className?: string;
  items: IPagerState<TItem> | IState<TItem[]>;
  scrollerMode?: InfiniteScrollerMode;
  stickyHeaderDisabled?: boolean;
}

interface State<TItem, TApiParams> {
  /**
   * Copy of the table definition used to generate this table. Used to detect when this.props.tableDefinition changes.
   */
  tableDefinition: ITableDefinition<TItem, TApiParams>;

  activeSort: SortState<TItem, TApiParams>;
  sortedItems: TItem[];
  tableDescriptionPrefix: string;
  viewportWidth: number;
  activeColumns: IColumnDefinition<TItem, TApiParams>[];
  visibleFilters: IFilterDefinition<TItem, TApiParams>[];
  numVisibleFilters: number;
  stickyState: number;
  error: string;
  currentFilters: TApiParams;
  isExportDialogOpen: boolean;
  tableDim: { width: number };
  stickyHeaderEnabled: boolean;
}

interface IScrollerItem {
  id: any;
}

const isValidSortState = (sortState: SortState<any, any>) => {
  return sortState && sortState.column && sortState.column.sorts && sortState.column.sorts.length > sortState.sortIndex;
};

export class DataTable<TItem extends IScrollerItem, TApiParams> extends React.Component<OwnProps<TItem, TApiParams>, Partial<State<TItem, TApiParams>>> {
  /**
   * Unique id per instance of this component
   */
  private instanceId: string;

  private scrollerHeaderWrap: HTMLDivElement;
  private filterRefs: HTMLDivElement[];
  private thStickyRefs: HTMLTableHeaderCellElement[];
  private thRealRefs: HTMLTableHeaderCellElement[];

  private getOnMount: boolean;
  private hasRunGetOnMount: boolean;

  constructor(props) {
    super(props);

    this.instanceId = getInstanceId();

    this.state = {
      activeSort: null,
      sortedItems: [],
      viewportWidth: null,
    };

    this.getOnMount = false;
    this.hasRunGetOnMount = false;
  }

  private getNextPageIndex = (): number | undefined => this.props.getPage ? (this.props.items as any).nextIndex : undefined;

  private getDefaultFilters = (): TApiParams => _.extend(
    {},
    ...(this.props.tableDefinition.filters || []).map(f => f.default),
    this.props.tableDefinition.defaultFilters,
  )

  /**
   * Safe wrapper for getPage, returns true if there was no error.
   */
  private getPage = async (index: number): Promise<boolean> => {
    this.setState({ error: null });

    try {
      // Collect all the parameters we need to send to the api
      // TODO keep filter state in infiniscroller context
      const activeSort = isValidSortState(this.state.activeSort)
        ? this.state.activeSort.column.sorts[this.state.activeSort.sortIndex]
        : null;

      const params: TApiParams = activeSort && activeSort.parameters
        ? _.extend({}, this.state.currentFilters, activeSort.parameters)
        : this.state.currentFilters;

      const response = await this.props.getPage(index, params);
      return true;
    }
    catch (e) {
      this.setState({ error: e.message });
      return false;
    }
  }

  public resetPages = _.debounce(async () => {
    if (!this.props.getPage) {
      return;
    }

    if (!(await this.getPage(-1))) {
      return;
    }

    if (this.props.scrollerMode === InfiniteScrollerMode.RetrieveAll) {
      while (true) {
        const nextIndex = this.getNextPageIndex();
        if (!nextIndex || !(await this.getPage(nextIndex))) {
          return;
        }
      }
    }
  }, 300, false);

  private async loadPage() {
    if (!this.props.getPage) {
      return;
    }

    const index = this.getNextPageIndex() || this.props.startPageIndex || 1;
    if (!(await this.getPage(index))) {
      return;
    }
  }

  private handleScroll = () => {
    if ((this.props.scrollerMode || InfiniteScrollerMode.EndlessOnScroll) !== InfiniteScrollerMode.EndlessOnScroll
      || !this.getNextPageIndex()
      || this.props.items.status === "PENDING"
      || this.state.error != null) {
      return;
    }

    const scrollHeight = window.document.body.clientHeight;
    const scrollBottom = window.scrollY + window.innerHeight;
    const scrollThreshold = 500;

    if (scrollBottom >= scrollHeight - scrollThreshold) {
      this.loadPage();
    }
  }

  private handleScrollDebounced = _.debounce(this.handleScroll, 300, false);

  /**
   * Adjust column widths for the available space
   */
  private handleResize = () => {
    const infiniScroller = document.getElementById(this.instanceId);
    if (!infiniScroller) {
      return;
    }

    const clientRect = infiniScroller.getBoundingClientRect();
    const newDim = { width: clientRect.width };
    const currentDim = this.state.tableDim;

    const widthChanged = !currentDim || Math.abs(currentDim.width - newDim.width) > 1;
    if (!widthChanged) {
      return; // Nowt to do
    }

    const newState: Partial<State<TItem, TApiParams>> = { tableDim: newDim };

    const activeColumns = this.getActiveColumnsForWidth(newDim.width);

    // Init columns
    if (!this.state.activeColumns || this.state.activeColumns.length !== activeColumns.length) {
      this.thStickyRefs = new Array(activeColumns.length);
      this.thRealRefs = new Array(activeColumns.length);

      newState.activeColumns = activeColumns;
    }

    // Make sticky table header match real table header widths
    for (let i = 0; i < activeColumns.length; i++) {
      if (this.thStickyRefs[i]) {
        this.thStickyRefs[i].style.width = `${this.thRealRefs[i].getBoundingClientRect().width}px`;
      }
    }

    const widths: number[] = [];

    // build an array of filter widths with added margin
    this.state.tableDefinition?.filters?.map((filter) => {
      widths.push((filter.width ?? 300) + 24);
    });

    // calculate how many filters we can show based on their widths
    widths.reduce((acc, cur, index) => {
      if ((acc + cur) < newDim.width) {
        newState.numVisibleFilters = index + 1;
        return acc + cur;
      }
    }, 0);

    newState.numVisibleFilters = this.state.visibleFilters ? newState.numVisibleFilters : 0;
    newState.stickyHeaderEnabled = this.shouldHeaderStick();
    this.setState(newState);
  }

  private handleResizeDebounced = _.debounce(this.handleResize, 400);

  private onStickyStateChange = (stickyState) => {
    const state: number = stickyState.status;
    if (state === 2) {
      this.setState({ stickyState: state });
    }
  }

  /**
   * No need to put sticky headers on tiny tables
   */
  private shouldHeaderStick = (): boolean => {
    if (isServer()) {
      return false;
    }

    if (this.props.stickyHeaderDisabled) {
      return false;
    }

    // If it's already stuck, then keep sticking
    if (this.state.stickyState === 2) {
      return true;
    }

    const infiniScroller = document.getElementById(this.instanceId);
    if (!infiniScroller) {
      return;
    }

    const clientRect = infiniScroller.getBoundingClientRect();
    return window.innerHeight < (clientRect.height * 1.2);
  }

  private handleFilterChange = (newFilter: TApiParams) => {
    // Can't use spread operator on generic types https://github.com/Microsoft/TypeScript/issues/15792#issuecomment-301267522
    const mergedFilters = _.extend({}, this.state.currentFilters, newFilter);

    this.setState({ currentFilters: mergedFilters }, () => {
      if (!this.props.tableDefinition.disableQueryString) {
        const queryStringObject = getQueryStringValues();

        const queryStringAndState = _.extend(queryStringObject, this.state.currentFilters)

        window.history.pushState(null, "", window.location.pathname + formatQueryString(queryStringAndState));

      }
      this.resetPages();
    });
  }

  /**
   * Build a user friendly description of what data is in the table
   * |header| sorted by |col desc| in |sort desc| order
   * TODO add filter descriptions
   */
  private buildTableStateDescription() {
    if (this.state.sortedItems.length < 1) {
      return "";
    }

    let description = this.state.tableDescriptionPrefix ? `${this.state.tableDescriptionPrefix.trim()}` : "";
    const visibleColumns = this.props.tableDefinition.columns.filter(colDef => colDef.isVisible ? colDef.isVisible() : true);

    const sortState = this.state.activeSort;
    if (sortState && sortState.column && sortState.column.sorts && sortState.column.sorts.length > sortState.sortIndex) {
      const col = sortState.column;
      const sort = col.sorts[sortState.sortIndex];

      description += `${description.length > 0 ? ", " : ""}sorted by ${col.description} ${sort.description}`;
    }

    return description;
  }

  /**
   * Perform client-side sorting if applicable.
   */
  private getSortCompare = (sortDef: ISortDefinition<TItem, TApiParams>) => (a: TItem, b: TItem) => {
    let val = 0;

    // Active sort
    if (sortDef && sortDef.compare) {
      val = sortDef.compare(a, b);
    }

    if (val !== 0) {
      return val;
    }

    // Fallback to default sort
    const defSort = this.props.tableDefinition.defaultSort;
    const defaultCompare = isValidSortState(defSort) ? defSort.column.sorts[defSort.sortIndex].compare : null;
    if (defaultCompare) {
      val = defaultCompare(a, b);
    }

    if (val !== 0) {
      return val;
    }

    // If default sort doesn't help, fallback to ID
    return a.id - b.id;
  }

  private changeSort = (clickedCol: IColumnDefinition<TItem, TApiParams>) => {
    if (!clickedCol.sorts) {
      return;
    }

    const selectedSort = this.state.activeSort;
    let newSort: SortState<TItem, TApiParams>;

    // Sort "coordinates" are a simple way to keep track of which is the active sort on the table.
    // Currently only one active sort is supported at any time.
    if (selectedSort && selectedSort.column === clickedCol) {
      const nextSortIndex = selectedSort.sortIndex + 1;
      if (nextSortIndex < clickedCol.sorts.length) {
        // if this column has the active sort, move to the next one.
        newSort = new SortState<TItem, TApiParams>(clickedCol, nextSortIndex);
      }
      else {
        // if the selected sort is the last in the column
        const defaultSort = this.props.tableDefinition.defaultSort;
        newSort = _.isEqual(selectedSort, defaultSort)
          ? new SortState<TItem, TApiParams>(clickedCol, 0) // we're already on the default, so let's loop round
          : defaultSort; // unselect this column and return to the default sort
      }
    }
    else if (clickedCol.sorts && clickedCol.sorts.length > 0) {
      // otherwise, switch to the first in this column.
      newSort = new SortState<TItem, TApiParams>(clickedCol, 0);
    }
    else {
      newSort = this.props.tableDefinition.defaultSort;
    }

    this.setState({ activeSort: newSort }, () => {
      if (!this.props.items.data) {
        return;
      }

      const sortDef = newSort.column.sorts[newSort.sortIndex];

      if (sortDef.parameters) {
        this.getPage(-1); // async - will complete at some point but we don't want to block state update for sort
      }

      if (sortDef.compare) {
        const sortFunc = this.getSortCompare(sortDef);
        const sortedItems = this.props.items.data.sort(sortFunc);

        this.setState({ sortedItems });
      }
    });
  }

  /**
   * Filter out hidden columns and columns that don't fit on the screen
   * @param viewportWidth Width of the space to fit in
   */
  private getActiveColumnsForWidth(viewportWidth: number): IColumnDefinition<TItem, TApiParams>[] {
    const cellPadding = 30; // ~2rem TODO this should probs be configurable

    const colMap = _.chain(this.props.tableDefinition.columns)
      .filter(col => col.isVisible ? col.isVisible() : true)
      .map((col, i) => ({ def: col, order: i }))
      .sortBy(x => x.def.shyness)
      .partition(x => x.def.shyness > 0)
      .value();

    let widthCounter = 0;
    const [shyColumns, columns] = colMap;

    for (const col of columns) {
      widthCounter += col.def.minWidth || COL_DEFAULT_MIN_WIDTH;
    }

    for (const col of shyColumns) {
      const colMinWidth = col.def.minWidth || COL_DEFAULT_MIN_WIDTH;

      // How outgoing is this column?
      if (col.def.shyness > 0 && viewportWidth != null && widthCounter + colMinWidth > viewportWidth) {
        continue;
      }

      widthCounter += colMinWidth + cellPadding;
      columns.push(col);
    }

    return _.chain(columns).sortBy(c => c.order).map(c => c.def).value();
  }

  private getColStyle = (def: IColumnDefinition<TItem, TApiParams>): React.CSSProperties => {
    const w = def.minWidth || COL_DEFAULT_MIN_WIDTH;
    return def.fixedWidth ? { width: w } : { minWidth: w };
  }

  private renderColumnHeadings = (
    columns: IColumnDefinition<TItem, TApiParams>[],
    refHandler: (ref: HTMLTableHeaderCellElement, index: number) => void,
  ): JSX.Element[] => {

    const tableDef = this.props.tableDefinition;

    const headings = columns.map((colDef, index) => {
      // If the table is sorted by this column, indicate so in the column heading
      const activeSort = isValidSortState(this.state.activeSort) && colDef === this.state.activeSort.column
        ? this.state.activeSort.column.sorts[this.state.activeSort.sortIndex]
        : null;

      let colClassName = colDef.colClassName || "";
      colClassName += (colDef.sorts && colDef.sorts.length) > 0 ? " sortable" : "";
      colClassName += activeSort ? " active-sort" : "";

      const description = activeSort
        ? `${colDef.description} (sorted ${activeSort.description})`
        : colDef.description;

      return (
        <th ref={thRef => refHandler && refHandler(thRef, index)}
          key={`${tableDef.title}-header-${index}`}
          style={this.getColStyle(colDef)}
          className={colClassName}
          title={description}
          onClick={() => this.changeSort(colDef)}>
          <header>{colDef.header} {activeSort && <span className={`icon-${activeSort.icomoon}`}></span>}</header>
        </th>
      );
    });

    if (tableDef.rowActionSet) {
      headings.push(<th className="row-actions" key={`${tableDef.title}-header-action`} />);
    }

    return headings;
  }

  private renderScrollerRows = (columns: IColumnDefinition<TItem, TApiParams>[]): JSX.Element[] => {
    const items = this.state.sortedItems;
    const tableDef = this.props.tableDefinition;

    const itemsToRender = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // onClick = onMouseDown + onMouseUp
      // so to enable text selection in the table, we can just ignore onMouseUps if the cursor has moved.
      // TODO wrap this in a mixin
      let mouseDown: { x: number, y: number };
      let handleMouseDown: (e: React.MouseEvent<any>) => void;
      let handleMouseUp: (e: React.MouseEvent<any>) => void;

      if (this.props.onClick) {
        handleMouseDown = (e: React.MouseEvent<any>) => {
          if (e.button === 0) {
            mouseDown = { x: e.clientX, y: e.clientY };
          }
        };

        handleMouseUp = (e: React.MouseEvent<any>) => {
          if (!mouseDown) {
            // click wasn't main button, or it started in another row, so it can be ignored
            return;
          }

          const threshold = 5;
          if (this.props.onClick
            && Math.abs(e.clientX - mouseDown.x) < threshold
            && Math.abs(e.clientY - mouseDown.y) < threshold) {
            this.props.onClick(item);
          }
          else {
            e.stopPropagation();
          }
        };
      }

      const hasRowActions = tableDef.rowActionSet != null;
      const rowKey = `${tableDef.title}-scroll-item-${item.id}`;
      const rowClasses = classNames(
        "data-row",
        tableDef.rowClassName ? tableDef.rowClassName(item) : null,
        { clickable: this.props.onClick != null },
      );

      itemsToRender.push(
        <tr key={rowKey} className={rowClasses}>
          {
            columns.map((colDef, index) => {
              const colStyle = {
                ...this.getColStyle(colDef),
                ...(colDef.colStyle ? colDef.colStyle(item) : {}),
              };

              const colClasses = classNames(
                colDef.colClassName,
                { "active-sort": isValidSortState(this.state.activeSort) && this.state.activeSort.column === colDef },
              );

              return (
                <td key={`${rowKey}-col-${index}`} className={colClasses} style={colStyle} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
                  {colDef.buildCell(item)}
                </td>
              );
            })
          }
          {hasRowActions &&
            <td className="row-actions">
              <DropdownButton renderDropdown={() => <RowActionList item={item} set={this.props.tableDefinition.rowActionSet} />} />
            </td>
          }
        </tr>,
      );

      if (i === items.length - 1 // end of list
        || (i % 20 === 19 && i > 0 && i < items.length - 5) // mid-list dividers
      ) {
        const hasMoreItems = this.getNextPageIndex();
        const colspan = hasRowActions ? columns.length + 1 : columns.length;

        const dividerRow = (
          <tr key={`${tableDef.title}-scroll-divider-${i}`} className="divider-row">
            <td colSpan={colspan}><small>{i + 1} / {items.length}{hasMoreItems ? "+" : ""}</small></td>
          </tr>
        );
        itemsToRender.push(dividerRow);
      }
    }

    return itemsToRender;
  }

  /**
   * Generate a CSV from all rows currently in state
   */
  private downloadCSV = async () => {
    if (!this.props.tableDefinition.csv) {
      return;
    }

    // http://stackoverflow.com/a/29304414/268555
    // Content is the csv generated string above
    const download = (content, fileName, mimeType) => {
      const a = document.createElement("a");
      mimeType = mimeType || "application/octet-stream";

      if (navigator.msSaveBlob) { // IE10
        navigator.msSaveBlob(new Blob([content], { type: mimeType }), fileName);
      }
      else if (URL && "download" in a) { // html5 A[download]
        a.href = URL.createObjectURL(new Blob([content], { type: mimeType }));
        a.setAttribute("download", fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      else {
        location.href = "data:application/octet-stream," + encodeURIComponent(content); // only this mime type is supported
      }
    };

    await new Promise(() => {
      const csv = renderCsv(this.props.tableDefinition.csv, this.state.sortedItems);
      const filename = `${slugify(this.props.tableDefinition.title)}.csv`;
      download(csv, filename, "text/csv;encoding:utf-8");
    });
  }

  private parseValue = (value, type = "") => {
    // Return a boolean
    if (value === "true" || value === "false") {
      return value === "true";
    }

    if (type === "datetime" && moment(value).isValid()) {
      return { type: "datetime", value } as IDateTime;
    }

    // Return a string if it is not a number
    // Or a number if it is (i.e. for an ID)
    return isNaN(Number(value)) ? value : Number(value);
  }

  // Gets the query string and returns an object key: value pairs
  // with keys matching the tableDefinition.filters, ignoring non-matches
  private queryStringToParams = () => {
    const parsedQueryString = getQueryStringValues();

    // If the query string key matches the current key,
    // Convert the comma separated string to an array
    return Object.keys(parsedQueryString).reduce((acc, curr) => {
      // Filter for the keys the table is expecting in the definitions
      const filter = this.props.tableDefinition.filters
        ? this.props.tableDefinition.filters.find(filterDef => Object.keys(filterDef.default).indexOf(curr) !== -1)
        : false;

      // If an expected filter was found, combine with the accumulator.
      // Use types[x] to check if it needs to be split
      // to get an array of strings or numbers i.e.
      // statuses=1,2 => [1, 2] // types['statuses'] === "array" set
      // animals=Sheep,Goats => ["Sheep", "Goats"] // types['animals'] === "array" set
      // query=Santiago,Valparaíso => "Santiago,Valparaíso" // no type set
      if (filter) {
        if (filter.types && filter.types[curr]) {
          return {
            ...acc,
            [curr]: filter.types[curr] === "array"
              ? parsedQueryString[curr].split(",").map(value => this.parseValue(value, filter.types[curr]))
              : this.parseValue(parsedQueryString[curr], filter.types[curr]),
          };
        }

        // No explicit types[x] set
        return {
          ...acc,
          [curr]: this.parseValue(parsedQueryString[curr]),
        };
      }

      return acc;
    }, {});
  }

  private prerender = (props: OwnProps<TItem, TApiParams>) => {
    const { tableDefinition, items } = props;

    const shouldReset = this.props.getPage != null && !_.isEqual(this.state.tableDefinition, tableDefinition);

    const tableDescriptionPrefix = tableDefinition.buildDescription ? tableDefinition.buildDescription(items.data) : "";

    let activeSort: SortState<TItem, TApiParams>;
    let sortedItems: TItem[];
    let currentFilters: TApiParams;

    if (shouldReset && sortedItems) {
      sortedItems = [];
      currentFilters = this.getDefaultFilters();
    }
    else {
      activeSort = isValidSortState(this.state.activeSort)
        ? this.state.activeSort
        : null;

      const sortDef = activeSort
        ? activeSort.column.sorts[activeSort.sortIndex]
        : null;

      sortedItems = items.data && sortDef && sortDef.compare
        ? items.data.sort(this.getSortCompare(sortDef))
        : items.data;

      const params = tableDefinition.disableQueryString ? {} : this.queryStringToParams();

      // Set current filters from query string
      // Or from default if query string is empty
      if (window.location.search && !this.getOnMount && !this.hasRunGetOnMount && !tableDefinition.disableQueryString) {
        // Combine current state and query string
        currentFilters = _.extend({}, this.state.currentFilters as TApiParams, params);

        this.getOnMount = true;
      } else {
        currentFilters = this.state.currentFilters
          ? _.extend({}, this.state.currentFilters as TApiParams, params)
          : this.getDefaultFilters();

        this.getOnMount = false;
      }
    }

    const stickyHeaderEnabled = this.shouldHeaderStick();
    const visibleFilters = (tableDefinition.filters || []).filter(fDef => fDef.enabled ? fDef.enabled() : true);
    this.filterRefs = new Array(visibleFilters.length);

    const newState: Partial<State<TItem, TApiParams>> = {
      sortedItems: sortedItems || [],
      tableDescriptionPrefix,
      visibleFilters,
      stickyHeaderEnabled,
      currentFilters,
    };

    if (!activeSort) {
      newState.activeSort = tableDefinition.defaultSort;
    }

    if (shouldReset) {
      newState.tableDefinition = tableDefinition;
      this.setState(newState, () => { this.resetPages(); this.handleResize(); });
    }
    else {
      this.setState(newState, () => {
        // Only run get if prerender is being run for the first time
        if (window.location.search && this.getOnMount && !this.hasRunGetOnMount) {
          this.getPage(-1);
          this.hasRunGetOnMount = true;

        }

        this.handleResize();
      });
    }
  }

  private resetFilter = (def: IFilterDefinition<TItem, TApiParams>) => {
    const currentFilters: TApiParams = null;
    this.setState({ currentFilters });
  }

  public async componentWillReceiveProps(nextProps: OwnProps<TItem, TApiParams>) {
    this.prerender(nextProps);
  }

  public componentDidMount() {
    this.prerender(this.props);

    // These only need to do work after the first render
    requestAnimationFrame(() => {
      window.addEventListener("scroll", this.handleScrollDebounced);
      window.addEventListener("resize", this.handleResizeDebounced);
    });
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScrollDebounced);
    window.removeEventListener("resize", this.handleResizeDebounced);
  }

  public componentDidUpdate() {
    // After each render, ensure that the sticky table header widths are in sync.
    requestAnimationFrame(() => {
      this.handleResizeDebounced();
    });
  }

  public render() {
    const tableDef = this.props.tableDefinition;
    const { activeColumns, visibleFilters } = this.state;
    if (!activeColumns || !visibleFilters) {
      // Instance ID must always be on the page
      return (
        <div id={this.instanceId}>
          <Loader />
        </div>
      );
    }

    // const tableStateDescription = this.buildTableStateDescription(); // TODO put this back into the UI
    const columnCount = tableDef.rowActionSet ? activeColumns.length + 1 : activeColumns.length;

    const filterElements = visibleFilters.map((filterDef) => (
      <div key={`${tableDef.title}-filter-${filterDef.keyFragment}`} className={`filter-control ${filterDef.keyFragment}`}>
        {filterDef.render(this.state.currentFilters, this.handleFilterChange)}
      </div>
    ));

    return (
      <div id={this.instanceId} className={classNames(this.props.className, "infinite-scroller-wrapper")}>
        <ErrorBoundary>
          {tableDef.csv &&
            <Dialog isOpen={this.state.isExportDialogOpen}
              title={`Export ${tableDef.title.toLowerCase()}`}
              onClose={() => this.setState({ isExportDialogOpen: false })}>
              <p>This button will export all the data currently on the page to a CSV file.</p>
              {this.getNextPageIndex() &&
                <p>Note: you've currently loaded {this.state.sortedItems.length} items. There are more items that match your current filters, but they need to be fetched from the server first. If you download the CSV now, it will only contain the {this.state.sortedItems.length} currently visible on the page. To include more items, close this dialog and scroll down the page.</p>
              }
              <hr />

              <h3>Options</h3>
              <p>Coming soon&trade;!</p>
              <hr />

              <PendingButton className="bg-cta" style={{ float: "right" }} leftIcon={Icon.Icomoon.download} children="Download CSV" onClick={this.downloadCSV} />
            </Dialog>
          }

          <div className="scroller-header-wrap" ref={x => this.scrollerHeaderWrap = x}>
            <Sticky activeClass="stuck"
              onStateChange={state => this.onStickyStateChange(state)}
              bottomBoundary={`#${this.instanceId}`}
              enabled={this.state.stickyHeaderEnabled}>
              <div className="scroller-header">
                <div className="table-controls">
                  <header>
                    <h3>{tableDef.title}</h3>
                  </header>
                  {(filterElements.length > 0 && this.state.numVisibleFilters > 0) &&
                    <div className="filters">
                      {filterElements.slice(0, this.state.numVisibleFilters)}
                    </div>
                  }
                  <div className="spacer" />
                  <div className="actions">
                    {(filterElements.length > 0 && (this.state.numVisibleFilters < filterElements.length)) &&
                      <DropdownButton
                        icomoon={Icon.Icomoon.filter2}
                        label="Filters"
                        renderDropdown={() => (
                          <div className="filters filter-dropdown-wrap">
                            {filterElements.slice(this.state.numVisibleFilters)}
                          </div>
                        )}
                      />
                    }

                    {(tableDef.tableActions || []).filter(a => a.isVisible ? a.isVisible() : true).map((action, i) => (
                      <Button key={`table-action-${i}`} leftIcon={action.icomoon} onClick={action.action}>{action.label}</Button>
                    ))}

                    {tableDef.csv &&
                      <Button leftIcon={Icon.Icomoon.download} onClick={() => this.setState({ isExportDialogOpen: true })}>
                        Export
                    </Button>
                    }
                  </div>
                </div>

                <table>
                  <thead>
                    <tr className="fixed-header">
                      {this.renderColumnHeadings(activeColumns, (ref, i) => this.thStickyRefs[i] = ref)}
                    </tr>
                  </thead>
                </table>
              </div>
            </Sticky>
          </div>

          <table>
            <thead>
              <tr>
                {this.renderColumnHeadings(activeColumns, (ref, i) => this.thRealRefs[i] = ref)}
              </tr>
            </thead>
            <tbody className="infinite-scroller-content">
              {(this.state.sortedItems.length < 1 && this.props.items.status === "OK") &&
                <tr className="info-row">
                  <td colSpan={columnCount} className="p-top-small p-bottom-small">
                    <p>There's nothing to show here.</p>
                  </td>
                </tr>
              }

              {this.renderScrollerRows(activeColumns)}

              {this.props.items.status === "PENDING" &&
                <tr className="loading-row">
                  <td colSpan={columnCount}>
                    <Loader />
                  </td>
                </tr>
              }

              {this.state.error &&
                <tr className="info-row">
                  <td colSpan={columnCount}>
                    <h4>Something went wrong!</h4>
                    <p>{this.state.error}</p>
                    <p><Button leftIcon={Icon.Icomoon.undo} onClick={() => this.loadPage()}>Retry...</Button></p>
                  </td>
                </tr>
              }

              {this.props.items.status === "OK" && this.getNextPageIndex() &&
                <tr className="info-row">
                  <td colSpan={columnCount} >
                    <Button leftIcon={Icon.Icomoon.download} onClick={() => this.loadPage()}>Show more...</Button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </ErrorBoundary>
      </div>
    );
  }
}
